import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClinicCtx } from '@/app/state';
import { Input, cx } from '@/components/ui';

/** Type-to-search list that also accepts free text (FR-EMR-4, FR-RX-1). */
export function SearchPicker<T>({ placeholder, search, render, onPick, onFreeText, autoFocus }: {
  placeholder: string;
  search: (q: string) => Promise<T[]>;
  render: (item: T) => { key: string; primary: string; secondary?: string; tone?: 'ok' | 'warn' };
  onPick: (item: T) => void;
  onFreeText?: (text: string) => void;
  autoFocus?: boolean;
}) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [items, setItems] = useState<T[]>([]);
  const [active, setActive] = useState(0);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let alive = true;
    void search(q).then((r) => { if (alive) { setItems(r); setActive(0); } });
    return () => { alive = false; };
  }, [q, search]);

  function onKey(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, items.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      const it = items[active];
      if (it) { onPick(it); setQ(''); }
      else if (onFreeText && q.trim()) { onFreeText(q.trim()); setQ(''); }
    }
  }

  return (
    <div ref={box} className="grid gap-2">
      <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey} placeholder={placeholder} autoFocus={autoFocus} />
      {items.length > 0 && (
        <ul className="max-h-56 overflow-y-auto rounded-lg border border-rule bg-surface">
          {items.map((it, i) => {
            const r = render(it);
            return (
              <li key={r.key}>
                <button type="button" onMouseEnter={() => setActive(i)} onClick={() => { onPick(it); setQ(''); }}
                  className={cx('flex w-full items-baseline justify-between gap-3 px-3 py-2 text-left', i === active && 'bg-accent-soft')}>
                  <span className="font-semibold">{r.primary}</span>
                  <span className={cx('text-sm', r.tone === 'warn' ? 'text-danger' : 'text-ink-2')}>{r.secondary}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {items.length === 0 && q.trim() && onFreeText && (
        <button type="button" className="rounded-lg border border-dashed border-rule px-3 py-2 text-left text-ink-2" onClick={() => { onFreeText(q.trim()); setQ(''); }}>
          {t('consult.free')}: <b>{q.trim()}</b>
        </button>
      )}
    </div>
  );
}

export interface Icd10Hit { code: string; title_en: string | null; title_my: string | null }

export function useIcd10Search() {
  const ctx = useClinicCtx();
  return useMemo(() => async (q: string): Promise<Icd10Hit[]> => {
    const s = q.trim();
    if (!s) return ctx.db.getAll<Icd10Hit>('select code, title_en, title_my from icd10_codes where is_common = 1 and active = 1 order by title_en limit 12');
    const like = `%${s.toLowerCase()}%`;
    return ctx.db.getAll<Icd10Hit>(
      `select code, title_en, title_my from icd10_codes where active = 1 and (lower(code) like ? or lower(title_en) like ? or title_my like ?)
       order by is_common desc, length(title_en) limit 15`, [like, like, like]);
  }, [ctx.db]);
}

export interface DrugHit { id: string; generic_name: string; brand_name: string | null; strength: string | null; form: string | null; dispense_unit: string; selling_price: number; qty_on_hand: number }

export function useDrugSearch() {
  const ctx = useClinicCtx();
  return useMemo(() => async (q: string): Promise<DrugHit[]> => {
    const like = `%${q.trim().toLowerCase()}%`;
    const where = q.trim() ? 'and (lower(d.generic_name) like ? or lower(d.brand_name) like ?)' : '';
    const params = q.trim() ? [ctx.clinicId, like, like] : [ctx.clinicId];
    return ctx.db.getAll<DrugHit>(
      `select d.id, d.generic_name, d.brand_name, d.strength, d.form, d.dispense_unit, d.selling_price,
              coalesce((select sum(m.qty) from stock_movements m join drug_batches b on b.id = m.batch_id where b.drug_id = d.id), 0) as qty_on_hand
       from drugs d where d.clinic_id = ? and d.active = 1 ${where} order by d.generic_name limit 15`, params);
  }, [ctx.db, ctx.clinicId]);
}

export interface Phrase { code: string; text_en: string; text_my: string; times_per_day: number | null; kind: string }

export function useDosagePhrases(kind: 'frequency' | 'instruction' | 'route' | 'dose_unit') {
  const ctx = useClinicCtx();
  const [rows, setRows] = useState<Phrase[]>([]);
  useEffect(() => {
    void ctx.db.getAll<Phrase>(
      `select code, text_en, text_my, times_per_day, kind from dosage_phrases where kind = ? and active = 1 and (clinic_id is null or clinic_id = ?) order by sort_order`,
      [kind, ctx.clinicId]).then(setRows);
  }, [ctx.db, ctx.clinicId, kind]);
  return rows;
}
